import { computed } from 'vue'

import { usePipe } from '@render/composables/usePipe'

// Default checkout status messages shared by PrimerCheckout and YunoCheckout.
// Each is backed by a global translation seeded by the API's TranslationSeeder
// (other languages are authored in the translations admin). Pipes built before
// a key existed — or languages whose row is missing/empty — fall back to the
// hardcoded English text: keep the two in sync (same contract as
// NetworkErrorBanner.vue's networkError key).
export const CHECKOUT_LOAD_ERROR_TRANSLATION_KEY = 'global.checkoutLoadError'
export const CHECKOUT_LOAD_ERROR_FALLBACK = 'Error loading checkout. Please try again.'
export const CHECKOUT_LOADING_TRANSLATION_KEY = 'global.checkoutLoading'
export const CHECKOUT_LOADING_FALLBACK = 'Loading checkout...'

type TipTapNode = { type?: string; text?: string; content?: TipTapNode[] }

// "Empty" must mean an empty-paragraph doc, not "no plain text": interpolation
// nodes (`type: 'in'`) carry their value in attrs and contribute nothing to a
// text walk, yet TipTapRenderer resolves them to real visible content — a
// localization authored as a single {{const.brand.x}} token is NOT empty.
function hasRenderableContent(node: TipTapNode): boolean {
  if (typeof node.text === 'string' && node.text.trim() !== '') return true
  if (node.type === 'in') return true
  return (node.content ?? []).some(hasRenderableContent)
}

// TipTap doc for the translated message, or undefined when the key is absent
// or its content is empty (a row created but never localized) — the caller
// then renders the hardcoded fallback instead.
function useTranslatedMessage(key: string) {
  const pipe = usePipe()
  return computed(() => {
    const doc = pipe.getTranslation(key)
    return doc && hasRenderableContent(doc as TipTapNode) ? doc : undefined
  })
}

export function useCheckoutLoadErrorMessage() {
  return useTranslatedMessage(CHECKOUT_LOAD_ERROR_TRANSLATION_KEY)
}

export function useCheckoutLoadingMessage() {
  return useTranslatedMessage(CHECKOUT_LOADING_TRANSLATION_KEY)
}
